import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * The accountant's calendar.
 *
 * Two objects, both of which the database has modelled since the first week
 * and neither of which the product has ever shown anybody: the month you close
 * and the return you file.
 *
 * The shapes here are deliberately close to the tables. These are not
 * dashboard summaries, they are the real thing a person signs their name to,
 * and a screen that paraphrases a return is a screen that can be wrong.
 */
export const PERIOD_STATUSES = ['open', 'soft_closed', 'closed'];
export const periodStatusSchema = z.enum(PERIOD_STATUSES);
export const PERIOD_STATUS_LABELS = {
    open: 'Open',
    soft_closed: 'Closing',
    closed: 'Closed',
};
export const financialPeriodSchema = z.object({
    id: uuidSchema,
    fiscalYear: z.number().int(),
    label: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    status: periodStatusSchema,
    closedAt: isoDateTimeSchema.nullable(),
    closedByName: z.string().nullable(),
    notes: z.string().nullable(),
});
/**
 * One reason a month is not finished.
 *
 * Blocking items make the books wrong. Advisory items make them uncertain, and
 * the difference is worth keeping visible: a person can close a month that is
 * uncertain as long as they know that is what they are doing.
 */
export const readinessItemSchema = z.object({
    kind: z.string(),
    severity: z.enum(['blocking', 'advisory']),
    itemCount: z.number().int(),
    headline: z.string(),
    detail: z.string(),
});
export const closePeriodRequestSchema = z.object({
    status: periodStatusSchema,
    notes: z.string().trim().max(500).optional(),
});
// ---------------------------------------------------------------------------
// Sign-off: the system prepares, a person signs
// ---------------------------------------------------------------------------
/** A draft with nothing standing between it and a signature. */
export const signOffCandidateSchema = z.object({
    transactionId: z.uuid(),
    transactionDate: z.string(),
    supplier: z.string().nullable(),
    category: z.string().nullable(),
    grossAmountMinor: z.string(),
    taxAmountMinor: z.string(),
    itcAmountMinor: z.string(),
    sourceDocumentId: z.uuid().nullable(),
});
/**
 * The ids the person was shown. The database signs these and only these, each
 * asked again at the moment of signing, so nothing is signed unseen.
 */
export const signOffRequestSchema = z.object({
    transactionIds: z.array(z.uuid()).min(1).max(1000),
});
export const signOffResultSchema = z.object({
    signed: z.number().int(),
    skipped: z.array(z.object({ id: z.uuid(), reasons: z.array(z.string()) })),
});
export const ensurePeriodsRequestSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2200),
});
// ---------------------------------------------------------------------------
// The return
// ---------------------------------------------------------------------------
export const HST_PERIOD_STATUSES = ['open', 'ready_to_file', 'filed', 'amended', 'closed'];
export const hstPeriodStatusSchema = z.enum(HST_PERIOD_STATUSES);
export const HST_PERIOD_STATUS_LABELS = {
    open: 'In progress',
    ready_to_file: 'Ready to file',
    filed: 'Filed',
    amended: 'Amended',
    closed: 'Closed',
};
/** Money crosses the wire as a string, the way every other figure here does. */
const minor = z.string();
export const hstReturnSchema = z.object({
    id: uuidSchema,
    periodStart: z.string(),
    periodEnd: z.string(),
    frequency: z.enum(['monthly', 'quarterly', 'annual']),
    status: hstPeriodStatusSchema,
    currencyCode: z.string(),
    filingDueOn: z.string().nullable(),
    filedOn: z.string().nullable(),
    confirmationNumber: z.string().nullable(),
    /** The GST34 itself, by its own line numbers. */
    line101Sales: minor,
    line103TaxCollected: minor,
    line105TotalTax: minor,
    line106Itc: minor,
    line108TotalItc: minor,
    line109NetTax: minor,
});
/** What is behind a figure. The reason anybody trusts the figure. */
export const returnDetailRowSchema = z.object({
    transactionId: uuidSchema,
    transactionLineId: uuidSchema,
    sourceDocumentId: uuidSchema.nullable(),
    occurredOn: z.string(),
    supplier: z.string(),
    description: z.string().nullable(),
    netAmountMinor: minor,
    taxAmountMinor: minor,
    itcAmountMinor: minor,
    fromVerifiedRule: z.boolean(),
});
export const fileReturnRequestSchema = z.object({
    filedOn: z.string().optional(),
    confirmationNumber: z.string().trim().max(60).optional(),
});
//# sourceMappingURL=close.js.map